// src/components/notifications/NotificationBootstrap.js
//
// Renders nothing. Two jobs:
//
// Keeps the queued notification window topped up — once on start, and again on
// every resume, because the queue is finite and drains.
//
// Nothing here prompts for permission. Scheduling happens for anyone who has
// already granted it (via the FCM onboarding) and silently no-ops for anyone
// who hasn't.
//
// While testing, it also hangs the helpers off window so they can be driven
// from the Safari inspector attached to the device:
//
//   __ggNotify.test()      -> fires real rotation copy in 5 seconds
//   __ggNotify.test(5, 4)  -> same, but message #4 specifically
//   __ggNotify.messages()  -> the whole rotation, no firing
//   __ggNotify.enable()    -> prompts for permission and schedules the window
//   __ggNotify.pending()   -> logs everything queued, soonest first
//   __ggNotify.disable()   -> cancels this module's schedule
//   __ggNotify.clear()     -> cancels EVERYTHING pending, including orphans
//                             left behind by a retired scheduler
//
// Remove the window block before shipping; everything above it stays.

import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import {
  refreshDailyNotifications,
  setDailyNotificationsEnabled,
  testNotification,
  pendingNotifications,
  notificationMessages,
  clearAllNotifications
} from '../../utils/appNotifications';

const NotificationBootstrap = () => {
  useEffect(() => {
    refreshDailyNotifications();

    let listener;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) refreshDailyNotifications();
    }).then((handle) => { listener = handle; });

    return () => { if (listener) listener.remove(); };
  }, []);

  // TEMPORARY test handles — remove before shipping.
  useEffect(() => {
    window.__ggNotify = {
      test: (seconds = 5, index = null) => testNotification(seconds, index),
      messages: () => {
        const list = notificationMessages();
        console.table(list);
        return list;
      },
      enable: () => setDailyNotificationsEnabled(true),
      disable: () => setDailyNotificationsEnabled(false),
      clear: () => clearAllNotifications(),
      pending: async () => {
        const list = await pendingNotifications();
        console.log(`[notifications] ${list.length} pending`);
        console.table(list);
        return list;
      }
    };
    return () => { delete window.__ggNotify; };
  }, []);

  return null;
};

export default NotificationBootstrap;